import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Skeleton,
} from "@heroui/react";
import { api } from "../../../lib/api-client";
import { getLocalizedValue } from "../../../utils";
import { LimePageHeader } from "../../../Components/LimePageHeader";
import { t } from "@lingui/core/macro";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { GetSubscription } from "@/server-types";
import { VariationsFilter } from "./components/VariationsFilter";
import { VariationCard } from "./components/VariationCard";
import { SubscriptionDurationUnit } from "@/server-types";
import SubscriptionFormDrawer from "./SubscriptionFormDrawer";

export const SubscriptionVariations = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQueryRaw = searchParams.get("q");
  const sortByRaw = searchParams.get("sortBy");
  const showInactiveRaw = searchParams.get("showInactive");
  const selectedSubscriptionIdRaw = searchParams.get("sid");

  const searchQuery = searchQueryRaw ?? "";
  const sortBy =
    (sortByRaw as "durationAsc" | "durationDesc" | "priceAsc" | "priceDesc") ||
    "durationAsc";
  const showInactive = showInactiveRaw === "true";
  const selectedSubscriptionId =
    selectedSubscriptionIdRaw != null
      ? parseInt(selectedSubscriptionIdRaw)
      : undefined;

  const { data: subscription, isLoading } = api.subscription.useGetSubscription(
    parseInt(subscriptionId ?? "-1", 10),
  );

  const filteredVariations = (() => {
    if (!subscription) return [];

    const convertToDays = (count: number, unit: SubscriptionDurationUnit) => {
      switch (unit) {
        case "day":
          return count;
        case "week":
          return count * 7;
        case "month":
          return count * 30;
        case "year":
          return count * 365;
        default:
          return count;
      }
    };

    const filtered = (
      subscription.subscriptionVariations as GetSubscription["response"]["subscriptionVariations"]
    )
      .filter((variation) => {
        const normalizedQuery = searchQuery.toLowerCase().replace(/\s+/g, "");
        const matchesName = getLocalizedValue(variation.nameLocalized)
          .toLowerCase()
          .replace(/\s+/g, "")
          .includes(normalizedQuery);
        const matchesDurationUnit = variation.durationUnit
          .toLowerCase()
          .replace(/\s+/g, "")
          .includes(normalizedQuery);
        const matchesdurationValue = variation.durationValue
          .toString()
          .includes(normalizedQuery);
        return (
          (showInactive || !variation.isDeleted) &&
          (!searchQuery ||
            matchesName ||
            matchesDurationUnit ||
            matchesdurationValue)
        );
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "durationAsc":
            return (
              convertToDays(a.durationValue, a.durationUnit) -
              convertToDays(b.durationValue, b.durationUnit)
            );
          case "durationDesc":
            return (
              convertToDays(b.durationValue, b.durationUnit) -
              convertToDays(a.durationValue, a.durationUnit)
            );
          case "priceAsc":
            return a.priceCents - b.priceCents;
          case "priceDesc":
            return b.priceCents - a.priceCents;
          default:
            return 0;
        }
      });

    return filtered;
  })();

  return (
    <>
      <LimePageHeader
        title={t`Naročnine`}
        subPage={{
          title: subscription
            ? getLocalizedValue(subscription.nameLocalized)
            : "",
          onBackButtonClick: () => navigate("/dashboard/subscriptions"),
        }}
      />

      <div className="mt-6 px-4 md:px-8">
        <VariationsFilter />

        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-4">
          {isLoading
            ? [...Array(6)].map((_, index) => (
                <Card key={index}>
                  <CardHeader className="flex items-center justify-between">
                    <Skeleton className="h-6 w-20 rounded-lg" />
                    <Skeleton className="h-7 w-7 rounded-lg" />
                  </CardHeader>
                  <CardBody>
                    <Skeleton className="h-8 w-32 rounded-lg" />
                    <Skeleton className="mt-2 h-7 w-24 rounded-lg" />
                  </CardBody>
                  <CardFooter></CardFooter>
                </Card>
              ))
            : filteredVariations.map((variation) => (
                <VariationCard
                  key={variation.id}
                  variation={variation}
                  subscription={subscription!}
                  onEdit={() => {
                    if (!subscriptionId) return;

                    setSearchParams((prev) => {
                      prev.set("sid", subscriptionId);
                      return prev;
                    });
                  }}
                />
              ))}
        </div>
      </div>

      <SubscriptionFormDrawer
        isOpen={selectedSubscriptionId != null}
        onSuccess={() => {
          setSearchParams((prev) => {
            prev.delete("sid");
            return prev;
          });
        }}
        subscriptionId={selectedSubscriptionId}
        initialTab="variations"
      />
    </>
  );
};
